import { type ActionType, loanActionTypes } from '@0xtorch/core'
import { and, eq } from 'drizzle-orm'
import { z } from 'zod'
import {
  type AssistantActionChange,
  type AssistantActionKey,
  assistantChangePayloadSchema,
} from '../../assistantSchemas'
import {
  actionTable,
  assistantProposalActionTable,
  assistantProposalTable,
  portfolioTable,
} from '../../schema'
import type { DatabaseWithTransaction, TransactionDatabase } from '../../types'
import {
  createAssistantActionKeyValue,
  parseAssistantChangePayloadJson,
} from './assistantWriteUtils'
import { deleteAssistantProposalsForActionKeys } from './deleteAssistantProposalsForActionKeys'

type ActionUpdate = Partial<
  Pick<
    typeof actionTable.$inferInsert,
    | 'type'
    | 'subType'
    | 'evidence'
    | 'locked'
    | 'generatedTx'
    | 'comment'
    | 'loanId'
    | 'crossId'
    | 'crossType'
  >
>

type PreparedActionUpdate = {
  readonly key: AssistantActionKey
  readonly values: ActionUpdate
}

/** 個別提案適用transactionの確定結果。 */
export type ApplyAssistantProposalChangesResult = {
  readonly changedActionCount: number
  readonly changedActions: readonly {
    readonly sourceId: string
    readonly order: number
  }[]
  readonly removedProposalIds: readonly string[]
  readonly validationStatus: 'valid' | 'invalid'
}

/** 承認primitiveの引数全体を検証するschema。 */
const applyInputSchema = z
  .object({
    proposalId: z.string().min(1),
    changes: assistantChangePayloadSchema,
  })
  .strict()

/**
 * nullable列の変更命令をDB値へ変換する。
 * @param params setまたはclear命令。
 * @returns set値またはnull。
 */
const createNullableValue = <T>({
  change,
}: {
  readonly change:
    | { readonly operation: 'set'; readonly value: T }
    | { readonly operation: 'clear' }
}): T | null => (change.operation === 'set' ? change.value : null)

/**
 * Action行と許可済み命令から、実際に値が変わる列だけを構築する。
 * @param params 現在Actionと対象変更。
 * @returns update不要ならundefined、必要なら更新列。
 */
const createActionUpdate = ({
  action,
  change,
}: {
  readonly action: typeof actionTable.$inferSelect
  readonly change: AssistantActionChange
}): ActionUpdate | undefined => {
  const values: ActionUpdate = {}
  const nextType: ActionType = change.fields.type?.value ?? action.type
  const typeChanged = nextType !== action.type
  if (typeChanged) {
    values.type = nextType
    if (action.subType !== null) {
      values.subType = null
    }
    if (!loanActionTypes.has(nextType) && action.loanId !== null) {
      values.loanId = null
    }
  }

  if (
    change.fields.evidence !== undefined &&
    change.fields.evidence.value !== action.evidence
  ) {
    values.evidence = change.fields.evidence.value
  }
  if (
    change.fields.locked !== undefined &&
    change.fields.locked.value !== action.locked
  ) {
    values.locked = change.fields.locked.value
  }
  if (
    change.fields.generatedTx !== undefined &&
    change.fields.generatedTx.value !== action.generatedTx
  ) {
    values.generatedTx = change.fields.generatedTx.value
  }
  if (change.fields.comment !== undefined) {
    const nextComment = createNullableValue({
      change: change.fields.comment,
    })
    if (nextComment !== action.comment) {
      values.comment = nextComment
    }
  }
  if (change.fields.crossId !== undefined) {
    const nextCrossId = createNullableValue({
      change: change.fields.crossId,
    })
    if (nextCrossId !== action.crossId) {
      values.crossId = nextCrossId
    }
  }
  if (change.fields.crossType !== undefined) {
    const nextCrossType = createNullableValue({
      change: change.fields.crossType,
    })
    if (nextCrossType !== action.crossType) {
      values.crossType = nextCrossType
    }
  }

  return Object.keys(values).length === 0 ? undefined : values
}

/**
 * browserでrule再検証済みの1提案を、全target不可分でActionへ適用する。
 * @param params DB、proposal ID、保存payloadと同一の許可済み変更。
 * @returns 実際に変更したActionと削除したproposalの確定結果。
 */
export const applyAssistantProposalChangesFromDatabase = async ({
  database,
  proposalId,
  changes,
  validate,
}: {
  readonly database: TransactionDatabase
  readonly proposalId: string
  readonly changes: z.input<typeof assistantChangePayloadSchema>
  readonly validate?: (parameters: {
    readonly database: TransactionDatabase
    readonly proposalId: string
  }) => Promise<'valid' | 'invalid'>
}): Promise<ApplyAssistantProposalChangesResult> => {
  const parsed = applyInputSchema.parse({ proposalId, changes })
  const proposalRows = await database
    .select()
    .from(assistantProposalTable)
    .where(eq(assistantProposalTable.id, parsed.proposalId))
    .limit(1)
  if (proposalRows.length === 0) {
    throw new Error('assistant proposal is not found')
  }
  const validationResult =
    (await validate?.({
      database,
      proposalId: parsed.proposalId,
    })) ?? 'valid'
  let preparedUpdates: readonly PreparedActionUpdate[] = []
  if (validationResult === 'valid') {
    const storedPayload = parseAssistantChangePayloadJson({
      changePayloadJson: proposalRows[0].changePayloadJson,
    })
    if (JSON.stringify(storedPayload) !== JSON.stringify(parsed.changes)) {
      throw new Error('assistant changes differ from stored proposal')
    }
    const targetRows = await database
      .select({ action: actionTable })
      .from(assistantProposalActionTable)
      .innerJoin(
        actionTable,
        and(
          eq(assistantProposalActionTable.sourceId, actionTable.sourceId),
          eq(assistantProposalActionTable.actionOrder, actionTable.order),
        ),
      )
      .where(
        and(
          eq(assistantProposalActionTable.proposalId, parsed.proposalId),
          eq(assistantProposalActionTable.role, 'target'),
        ),
      )
    const actionByKey = new Map<string, typeof actionTable.$inferSelect>()
    for (const row of targetRows) {
      actionByKey.set(
        createAssistantActionKeyValue({
          sourceId: row.action.sourceId,
          actionOrder: row.action.order,
        }),
        row.action,
      )
    }
    if (actionByKey.size !== parsed.changes.actions.length) {
      throw new Error('assistant proposal target set has changed')
    }
    const nextUpdates: PreparedActionUpdate[] = []
    for (const change of parsed.changes.actions) {
      const key: AssistantActionKey = {
        sourceId: change.sourceId,
        actionOrder: change.actionOrder,
      }
      const action = actionByKey.get(createAssistantActionKeyValue(key))
      if (action === undefined) {
        throw new Error('assistant proposal target is not found')
      }
      const values = createActionUpdate({ action, change })
      if (values !== undefined) {
        nextUpdates.push({ key, values })
      }
    }
    preparedUpdates = nextUpdates
  }
  for (const update of preparedUpdates) {
    await database
      .update(actionTable)
      .set(update.values)
      .where(
        and(
          eq(actionTable.sourceId, update.key.sourceId),
          eq(actionTable.order, update.key.actionOrder),
        ),
      )
  }
  const removedIdSet = new Set<string>()
  if (validationResult === 'valid' && preparedUpdates.length > 0) {
    const relatedProposalIds = await deleteAssistantProposalsForActionKeys({
      database,
      actionKeys: preparedUpdates.map((update) => update.key),
    })
    for (const relatedProposalId of relatedProposalIds) {
      removedIdSet.add(relatedProposalId)
    }
    await database.update(portfolioTable).set({ isTxGenerated: false })
  }
  const deletedRows = await database
    .delete(assistantProposalTable)
    .where(eq(assistantProposalTable.id, parsed.proposalId))
    .returning({ id: assistantProposalTable.id })
  for (const deletedRow of deletedRows) {
    removedIdSet.add(deletedRow.id)
  }

  return {
    changedActionCount: preparedUpdates.length,
    changedActions: preparedUpdates.map((update) => ({
      sourceId: update.key.sourceId,
      order: update.key.actionOrder,
    })),
    removedProposalIds: [...removedIdSet],
    validationStatus: validationResult,
  }
}

/**
 * browserで再検証した1提案を1 transactionでActionへ適用する。
 * @param params DB、proposal ID、変更内容、最終validation。
 * @returns 変更Actionと削除proposalの確定結果。
 */
export const applyAssistantProposalChanges = async ({
  database: { transaction },
  ...parameters
}: {
  readonly database: DatabaseWithTransaction
  readonly proposalId: string
  readonly changes: z.input<typeof assistantChangePayloadSchema>
  readonly validate?: (parameters: {
    readonly database: TransactionDatabase
    readonly proposalId: string
  }) => Promise<'valid' | 'invalid'>
}): Promise<ApplyAssistantProposalChangesResult> => {
  let result: ApplyAssistantProposalChangesResult | undefined
  await transaction([
    (database) => [
      applyAssistantProposalChangesFromDatabase({
        database,
        ...parameters,
      }).then((value) => {
        result = value
      }),
    ],
  ])
  if (result === undefined) {
    throw new Error('assistant proposal transaction returned no result')
  }
  return result
}
